import { ShieldCheck, ShieldAlert, ShieldQuestion, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { sourceTypeLabel } from "@/lib/integration-meta";
import type { EntraLicenseState } from "@/types/api";

/**
 * Entra ID health / licence panel (#177c).
 *
 * Presentational only — the caller (ConnectMicrosoft) owns the hooks. Renders
 * the 177b licence signal into one of three coverage states and exposes a
 * "re-check licence" action (which the caller wires to
 * `POST /integrations/<id>/test`, not a re-consent).
 *
 * Coverage is driven by `signin_logs_enabled`:
 *   true  → P1/P2: sign-in threat detection on (full coverage)
 *   false → confirmed Free: directory-audit-only (degrade + P1 upsell)
 *   null  → indeterminate: licence not yet known — we deliberately claim
 *           NEITHER full nor degraded, since a false "audit-only" would tell a
 *           paying P2 customer their sign-in detection is off.
 */

/**
 * What the lite-pack covers — shared copy with the setup guide (177e).
 * Directory-audit categories are available on every edition; risky/failed
 * sign-ins are gated on a P1/P2 licence, so they're listed separately and
 * only claimed as monitored when `signin_logs_enabled` is true.
 */
const AUDIT_SCOPE = [
  "Role & group changes",
  "Conditional Access policy changes",
  "Consent grants (app permissions)",
];
const SIGNIN_SCOPE = "Risky & failed sign-ins";

function tierLabel(tier: string | null): string {
  switch (tier) {
    case "p2":
      return "Entra ID P2";
    case "p1":
      return "Entra ID P1";
    case "none":
      return "Free / Microsoft 365";
    default:
      return "Unknown";
  }
}

interface EntraLicensePanelProps {
  entra: EntraLicenseState;
  /** Trigger a fresh licence re-probe (POST /integrations/<id>/test). */
  onRecheck: () => void;
  recheckPending?: boolean;
  recheckError?: string | null;
  /** Set when the entra_id integration id can't be resolved to call /test. */
  recheckDisabled?: boolean;
}

export function EntraLicensePanel({
  entra,
  onRecheck,
  recheckPending = false,
  recheckError = null,
  recheckDisabled = false,
}: EntraLicensePanelProps) {
  const signin = entra.signin_logs_enabled;

  return (
    <div data-testid="entra-license-panel" className="rounded-md border p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">{sourceTypeLabel("entra_id")}</h4>
        <Badge variant="outline">{tierLabel(entra.license_tier)}</Badge>
      </div>

      {/* Coverage state — capability/future framing only (ingestion is dormant
          until go-live, so no present-tense "we're collecting" claims). */}
      {signin === true && (
        <div data-testid="entra-coverage-full" className="flex items-start gap-2 text-sm">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-green-500" />
          <span>
            <span className="font-medium text-green-500">Full coverage.</span>{" "}
            Your {tierLabel(entra.license_tier)} licence includes sign-in threat detection
            alongside directory audit logs.
          </span>
        </div>
      )}

      {signin === false && (
        <div
          data-testid="entra-coverage-degraded"
          className="space-y-2 rounded-md bg-yellow-500/10 p-3 text-sm"
        >
          <div className="flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
            <span>
              <span className="font-medium text-yellow-500">Audit-only coverage.</span>{" "}
              Your licence covers directory audit logs. Risky &amp; failed sign-in monitoring
              needs an Entra ID P1 (or P2) licence.
            </span>
          </div>
          <p className="pl-6 text-muted-foreground">
            Upgrade to Entra ID P1 to enable sign-in threat detection.
          </p>
        </div>
      )}

      {signin == null && (
        <div data-testid="entra-coverage-unknown" className="flex items-start gap-2 text-sm">
          <ShieldQuestion className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            Licence not yet determined — re-check to confirm whether sign-in threat detection is
            available.
          </span>
        </div>
      )}

      {/* Lite-pack scope. Sign-ins are only claimed as monitored on P1/P2;
          otherwise they're shown as a gated capability, never overclaimed. */}
      <div className="text-sm">
        <p className="mb-1 text-muted-foreground">We monitor:</p>
        <ul data-testid="entra-monitored" className="list-disc pl-5 space-y-0.5 text-muted-foreground">
          {AUDIT_SCOPE.map((item) => (
            <li key={item}>{item}</li>
          ))}
          {signin === true && <li>{SIGNIN_SCOPE}</li>}
        </ul>
        {signin !== true && (
          <p data-testid="entra-signin-conditional" className="mt-1 text-muted-foreground">
            {SIGNIN_SCOPE} — available with Entra ID P1/P2.
          </p>
        )}
      </div>

      {/* Honest state — no live-data promise (ingestion is dormant until go-live). */}
      <p className="text-xs text-muted-foreground">
        Monitoring activates at go-live; once configured there's nothing more to do here.
      </p>

      {recheckError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{recheckError}</span>
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={onRecheck}
        disabled={recheckPending || recheckDisabled}
      >
        <RefreshCw className={`h-4 w-4 mr-1.5 ${recheckPending ? "animate-spin" : ""}`} />
        {recheckPending ? "Checking..." : "Re-check licence"}
      </Button>
    </div>
  );
}
